/* eslint-disable no-unused-vars */
import { isAfter, isBefore, isEqual, isWithinInterval, subYears, format } from 'date-fns'

export const formatS2kData = (currS2k, s2kScores, encounterDate, pendS2k) => {
  return [
    {
      id: 'SLEDAI-2K',
      data: [
        ...s2kScores.map((score) => {
          const date = score.encounterDate.slice(0, score.encounterDate.indexOf('T')).split('-')
          return ({
            x: `${parseInt(date[1])}/${date[2]}/${date[0]}`,
            y: score.score
          })
        }),
        {
          x: format(new Date(transformDate(encounterDate)), 'M/dd/yyyy'),
          y: pendS2k ? null : currS2k.reduce((acc, curr) => acc + curr.score, 0)
        }].sort((a, b) => isAfter(new Date(a.x), new Date(b.x)) ? 1 : -1)
    }
  ]
}

export const formatPatientData = (selectedIds, patientData, columns) => {
  const rgbaColors = ['rgba(79, 125, 147, 1)', 'rgba(101, 94, 163, 1)', 'rgba(62, 109, 54, 1)']
  const data = [].concat(...patientData.map((pd) => pd.children))

  const datasets = selectedIds.map((id, index) => {
    const dataPoints = data.find((d) => id === d.id)
    return {
      label: dataPoints.title,
      data: columns.map((date) => dataPoints[date] !== undefined ? dataPoints[date] : null),
      backgroundColor: rgbaColors[index],
      borderColor: rgbaColors[index],
      borderWidth: 1,
      fill: false,
      pointBorderWidth: 2,
      lineTension: 0
    }
  })
  return { labels: columns, datasets }
}

export const formatPatientMedications = (medications, encounterDate, years) => {
  const timeRangeEnd = new Date(encounterDate)
  const timeRangeStart = subYears(timeRangeEnd, years)

  const meds = [].concat(
    ...medications.map((medication) =>
      medication.medications.filter((med) => {
        const startDate = med.startDate
        const endDate = med.endDate ? med.endDate : med.startDate
        return isWithinInterval(timeRangeStart, { start: new Date(startDate), end: new Date(endDate) }) || isWithinInterval(new Date(startDate), { start: timeRangeStart, end: timeRangeEnd })
      })
    )
  )
  return [{
    data: meds.map((med) => {
      const endDate = med.endDate
        ? isAfter(new Date(med.endDate), timeRangeEnd)
          ? timeRangeEnd.getTime()
          : new Date(med.endDate).getTime()
        : timeRangeEnd.getTime()
      return ({
        x: formatLabel(med.name, 15).split('@'),
        y: [new Date(med.startDate).getTime(), endDate]
      })
    })
  }]
}

export const formatLabel = (label, limit) => {
  let str = ''
  for (let i = 0, count = 0; i < label.length; i += 1) {
    if (count >= limit && label[i] === ' ') {
      count = 0
      str += '@'
    } else {
      count += 1
      str += label[i]
    }
  }
  return str
}

export const buildS2kItems = (items) => {
  const itemNames = items.map((item) => item.item.name)
  return itemNames.join(', ')
}

export const formatRuleActions = (rule, actions) => {
  const patientEds = rule.patientEdContent.map((patientEd) => {
    return {
      id: patientEd.contentId,
      url: patientEd.content.url,
      title: patientEd.content.title
    }
  })

  const ruleActions = []

  rule.actionIds.forEach((actionId) => {
    const action = actions.find((action) => action.key === actionId)
    ruleActions.push(action)
  })

  return {
    patientEds,
    ruleActions
  }
}

export const filterActionsByActionType = (rules, actions, subActions, actionType) => {
  let activeActions = []

  for (let i = 0; i < rules.length; i += 1) {
    const rule = rules[i]
    for (let k = 0; k < rule.activeActionIds.length; k += 1) {
      const activeActionId = rule.activeActionIds[k]
      const action = actions.find((a) => a.key === activeActionId && a.actionType === actionType)
      if (action) {
        const activeSubActions = action.subActionIds.filter((subActionId) => rule.activeSubActionIds.includes(subActionId))

        if (activeSubActions.length > 0) {
          for (let j = 0; j < activeSubActions.length; j += 1) {
            const activeSubAction = activeSubActions[j]
            const subAction = subActions.find((sa) => sa.key === activeSubAction && sa.actionType === actionType)
            if (subAction) {
              const activeActionIndex = activeActions.findIndex((activeAction) => activeAction.actionId === subAction.actionId)
              if (activeActionIndex > -1) {
                activeActions = [
                  ...activeActions.slice(0, activeActionIndex),
                  {
                    ...activeActions[activeActionIndex],
                    keys: [...activeActions[activeActionIndex].keys, subAction.key]
                  },
                  ...activeActions.slice(activeActionIndex + 1)
                ]
              } else {
                activeActions.push({
                  ...subAction,
                  isSubAction: true,
                  keys: [subAction.key]
                })
              }
            }
          }
        } else {
          const activeActionIndex = activeActions.findIndex((activeAction) => activeAction.actionId === action.actionId)
          if (activeActionIndex > -1) {
            activeActions = [
              ...activeActions.slice(0, activeActionIndex),
              {
                ...activeActions[activeActionIndex],
                keys: [...activeActions[activeActionIndex].keys, action.key]
              },
              ...activeActions.slice(activeActionIndex + 1)
            ]
          } else {
            activeActions.push({
              ...action,
              isSubAction: false,
              keys: [action.key]
            })
          }
        }
      }
    }
  }

  if (activeActions.some((activeAction) => activeAction.name === 'Order Lupus Activity Panel')) {
    const ordersToRemove = ['Order: CBC Differential', 'Order: ALT', 'Order: AST', 'Order: BUN', 'Order: Creatinine', 'Order: Urinalysis']
    activeActions = activeActions.filter((activeAction) => !ordersToRemove.includes(activeAction.description))
  }
  return activeActions
}

export const getTotalS2kScore = (s2kScores) => Object.keys(s2kScores).reduce((acc, key) => acc + s2kScores[key].score, 0)

export const getPreviousScore = (s2kScores) => {
  let previousScore = 'N/A'
  let previousEncounterDate = ''
  if (s2kScores.length > 0) {
    previousScore = s2kScores[s2kScores.length - 1].score
    previousEncounterDate = format(new Date(s2kScores[s2kScores.length - 1].encounterDate), 'MM/dd/yyyy')
  }
  return { value: previousScore, date: previousEncounterDate }
}

export const formatMedications = (medications) => {
  return medications.map((medication) => `\u2022 ${medication.name}`).join('\n')
}

export const formatFlareStatement = (flare) => {
  return flare.every((flareValue) => flareValue.value === '') ? 'Patient has no flare.' : 'Patient in flare.'
}

export const getApiServer = () => {
  const { protocol, hostname } = window.location
  return hostname === 'localhost' ? 'http://localhost:5000' : `${protocol}//${hostname}`
}

export const buildAssessmentNote = (note, conditions, encounterDate) => {
  const lupusCodes = ['M32.9', 'M32.19']
  const lupusConditions = conditions.map((c) => c.code).filter((code, index, self) => lupusCodes.includes(code) && self.indexOf(code) === index)
  let noteBuilder = ''
  // Lupus monitor
  noteBuilder += 'Active monitoring of '
  noteBuilder += lupusConditions.map((lc) => {
    const condition = conditions.find((condition) => condition.code === lc)
    return condition.name ? `${condition.name} (${condition.code})` : condition.code
  }).join(', ')
  noteBuilder += ` ${encounterDate}`
  noteBuilder += '\n'
  noteBuilder += note.monitoring.map((monitor) => monitor.message).join('\n')
  noteBuilder += '\n\n'

  // Guidelines
  noteBuilder += 'Patient not meeting widely accepted guidelines: \n\n'
  noteBuilder += note.guidelines.map((guideline) => guideline.message).join('\n')
  noteBuilder += '\n\n'

  // Recommendations
  noteBuilder += 'Recommendations discussed with patient: \n\n'
  noteBuilder += note.recommendations.map((recommendation) => recommendation.message).join('\n')

  return noteBuilder
}

export const buildOrdersRequest = (orders) => orders.map((order) => order.orderTemplateId)

export const uniqueValuesInArrOfObjs = (arr, key) => [...new Set(arr.map((a) => a[key]))]

export const filterActionsByOrders = (rules, actions, orders, actionType) => {
  const selectedOrders = filterActionsByActionType(rules, actions, actionType)
  const actionOrders = []
  orders.forEach((order) => {
    const actionToSelect = selectedOrders.find(action => action.actionId === order.actionId && action.actionType === actionType)
    if (actionToSelect !== undefined) {
      actionOrders.push(actionToSelect)
    }
  })
  return actionOrders
}

export const formatMiddleName = (name) => name ? `${name.charAt(0)}.` : ''

export const filterFinishedActionsByActionType = (actions, key) => actions.filter((action) => action.action.actionType.name === key)
  .filter((action, index, actions) => actions.findIndex((a) => a.actionId === action.actionId) === index)

export const getFlareIndex = (flare) => {
  if (flare.some((f) => f.value === 'Severe')) {
    return 'Severe'
  }

  if (flare.every((f) => f.value === '')) {
    return 'No Flare'
  }

  return 'Mild/Moderate'
}

export const capitalizeFirstLetter = (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()

export const isObjectEmpty = (obj) => Object.keys(obj).length === 0

export const isSuperAdmin = (user) => {
  let isSuperAdmin = true

  if (!isObjectEmpty(user)) {
    isSuperAdmin = user.role.roleId === 1
  }

  return isSuperAdmin
}

const buildS2kScores = (s2k, s2kScores, encounterDate) => {
  let obj = {
    id: '0-1',
    title: 'SLEDAI-2K',
    [format(new Date(encounterDate), 'MM/dd/yyyy')]: s2k.reduce((acc, curr) => {
      if (curr.checked) {
        return acc + curr.points
      }
      return acc
    }, 0)
  }

  s2kScores.forEach((s2kScore) => {
    obj[format(new Date(s2kScore.encounterDate), 'MM/dd/yyyy')] = s2kScore.score
  })

  return obj
}

const buildPgaScores = (pga, pgas, encounterDate) => {
  let obj = {
    id: '0-2',
    title: 'Physician\'s Global Assessment',
    [format(new Date(encounterDate), 'MM/dd/yyyy')]: parseFloat(pga).toFixed(2)
  }
  pgas.forEach((p) => {
    obj[format(new Date(p.encounter.date), 'MM/dd/yyyy')] = parseFloat(p.value).toFixed(2)
  })
  return obj
}

const buildDamageScores = (damage, damages, encounterDate) => {
  let obj = {
    id: '0-3',
    title: 'Damage Index',
    [format(new Date(encounterDate), 'MM/dd/yyyy')]: damage.reduce((acc, curr) => {
      return acc + curr.rows.reduce((acc2, curr2) => acc2 + curr2.score, 0)
    }, 0)
  }

  damages.forEach((d) => {
    obj[format(new Date(d.encounter.date), 'MM/dd/yyyy')] = d.score
  })
  return obj
}

export const buildPatientData = (columns, labs, medications, procedures, s2kItems, s2k, s2kScores, encounterDate, pgas, pga, damage, damages, years) => {
  const encDate = new Date(encounterDate)
  const trendGraph = {
    id: 0,
    title: 'Assessments',
    children: [
      buildS2kScores(s2k, s2kScores, encounterDate),
      buildPgaScores(pga, pgas, encounterDate),
      buildDamageScores(damage, damages, encounterDate)
    ]
  }

  const uniqueObservations = uniqueValuesInArrOfObjs(labs, 'code')
  const buildObservations = {
    id: 1,
    title: 'Observations',
    children: uniqueObservations.reduce((acc, curr, index) => {
      const observationValues = labs.filter((l) => {
        const compareEndDate = encDate
        const compareStartDate = subYears(compareEndDate, years)
        return l.code === curr && isWithinInterval(new Date(l.effectiveDate), { start: compareStartDate, end: compareEndDate })
      })
      if (observationValues.length > 0) {
        let obj = {
          id: `1-${index + 1}`,
          title: observationValues[0].codeDescription === null ? curr : observationValues[0].codeDescription
        }

        observationValues.forEach((observationValue) => {
          obj[format(new Date(observationValue.effectiveDate), 'MM/dd/yyyy')] = observationValue.value
        })

        return [...acc, obj]
      }
      return acc
    }, [])
  }

  const filteredProcedures = procedures.filter((proc) => {
    const compareEndDate = encDate
    const compareStartDate = subYears(compareEndDate, years)
    return isWithinInterval(new Date(proc.performedDate), { start: compareStartDate, end: compareEndDate })
  })
  const uniqueProcedures = uniqueValuesInArrOfObjs(filteredProcedures, 'name')
  const buildProcedures = {
    id: 2,
    title: 'Procedures',
    children: uniqueProcedures.reduce((acc, curr, index) => {
      const procedureValues = filteredProcedures.filter((p) => p.name === curr)
      if (procedureValues.length > 0) {
        let obj = {
          id: `2-${index + 1}`,
          title: curr
        }

        procedureValues.forEach((procedureValue) => {
          obj[format(new Date(procedureValue.performedDate), 'MM/dd/yyyy')] = 'X'
        })
        return [...acc, obj]
      }
      return acc
    }, [])
  }

  const buildMeds = {
    id: 3,
    title: 'Lupus (SLE) Medications',
    children: medications.map((medication, index) => {
      let obj = {
        id: `3-${index}`,
        title: medication.name.split(': ')[1]
      }
      columns.forEach((column) => {
        const medsTaken = medication.medications.filter((med) => isWithinInterval(new Date(column), { start: new Date(med.startDate), end: med.endDate ? new Date(med.endDate) : new Date() }))

        if (medsTaken.length > 0) {
          medsTaken.forEach((medTaken) => {
            const medCode = medication.codes.find((code) => medTaken.code === code.code)
            if (medCode.dosage && medTaken.supplyDuration && medTaken.dispenseQuantityValue) {
              const duration = medTaken.supplyDuration !== null ? medTaken.supplyDuration : 1
              obj[column] = (parseFloat(medCode.dosage) * (medTaken.dispenseQuantityValue / duration))
            }
          })
        }
      })
      return obj
    })
  }

  const buildS2k = {
    id: 4,
    title: 'SLEDAI-2K',
    children: s2kItems.map((item, index) => {
      const s2kItem = s2k.find((s) => s.id === item.itemId)
      let obj = {
        id: `4-${index + 1}`,
        title: item.name,
        [format(new Date(encounterDate), 'MM/dd/yyyy')]: s2kItem.checked
      }

      s2kScores.forEach((score) => {
        const foundItem = score.items.find((i) => i.itemId === item.itemId)
        obj[format(new Date(score.encounterDate), 'MM/dd/yyyy')] = foundItem !== undefined
      })

      return obj
    })
  }
  return [trendGraph, buildObservations, buildProcedures, buildMeds, buildS2k]
}

export const formatConditions = (conditions, encounterDate) => {
  if (conditions.length === 0) return conditions

  const encounter = new Date(encounterDate)
  const { entry } = conditions

  const patientConditions = entry.map((e) => e.resource)
  const filteredConditions = patientConditions.filter((pc) => {
    const conditionStartDate = new Date(pc.onsetPeriod.start)
    return isBefore(conditionStartDate, encounter) || isEqual(conditionStartDate, encounter)
  })

  return filteredConditions
}

export const formatProcedures = (procedures, encounterDate) => {
  if (!Array.isArray(procedures)) return []

  if (procedures.length === 0) return []

  const encounter = new Date(encounterDate)

  const filteredProcedures = procedures.map((p) => {
    const performedDate = new Date(p.performedDateTime)
    return isBefore(performedDate, encounter) || isEqual(performedDate, encounter)
  })

  return filteredProcedures
}

export const formatMeds = (medications, encounterId) => {
  if (medications.length === 0) return []

  return medications.filter((medication) => {
    if (medication.reference) {
      const medicationEncounter = medication.encounter.reference ? medication.encounter.reference.split('/')[1] : medication.encounter.identifier.value
      return medicationEncounter === encounterId
    }
  })
}

export const formatDate = (date) => format(new Date(date), 'yyyy-MM-dd')

export const formatObservations = (observations, encounterDate, encounterId) => {
  if (observations.length === 0) return []

  const encounter = new Date(encounterDate)
  return observations
    .filter((o) => o.resource.resourceType === 'Observation')
    .filter((observation) => {
      const { resource } = observation
      const observationEncounterId = resource.encounter.reference ? resource.encounter.reference : resource.encounter.identifier.value
      const effectiveDate = new Date(formatDate(resource.effectiveDateTime))
      return (isBefore(effectiveDate, encounter) || isEqual(effectiveDate, encounter)) && observationEncounterId === encounterId
    })
}

export const transformDate = (date) => date.replace(/-/g, '/').substring(0, date.indexOf('T'))